import type { Filter } from "@/shared/components/ResourceView";
import type { AdminFeedbackFilters } from "@/shared/domain/admin/hooks/useAdminFeedback";
import type {
  FeedbackCategory,
  FeedbackSentiment,
} from "@/shared/domain/agentThread";

/**
 * Maps the committed ResourceView filter chips to the admin-feedback query.
 * The endpoint takes positive, exact values per field, so a field gets values
 * only from exactly one compatible chip:
 * - an enum field, from an "is any of" (``ANY_OF``) chip without "Not set":
 *   every ticked value, matched as an OR;
 * - ``thread_id``, from an ``EQUALS`` chip with one value;
 * - a boolean field, from a chip with one value.
 *
 * Anything else ("is none of", "Not set", a second chip on the same field)
 * leaves the field unfiltered rather than being coerced or inverted. The check
 * runs here, on read, so it also covers chips that arrive through a deep link.
 */
export function filtersToFeedbackFilters(
  filters: Filter[],
): AdminFeedbackFilters {
  const singleChip = (field: string): Filter | undefined => {
    const matches = filters.filter((f) => f.field === field);
    return matches.length === 1 ? matches[0] : undefined;
  };
  const enumValues = (field: string): string[] | undefined => {
    const f = singleChip(field);
    // An enum is a checklist, so "is" over one value is a one-member ANY_OF
    // (a stored EQUALS is read as one; see `upgradeLegacyFilter`).
    return f?.type === "enum" &&
      f.comparator === "ANY_OF" &&
      f.values.length > 0 &&
      f.includeNull !== true
      ? f.values.map((v) => v.value)
      : undefined;
  };
  const stringSingle = (field: string): string | undefined => {
    const f = singleChip(field);
    // The filter UI offers only ``EQUALS``; any other comparator (from a deep
    // link) would otherwise be sent as the endpoint's exact-match param, so
    // "Session ≠ X" would return only X.
    return f?.type === "string" &&
      f.comparator === "EQUALS" &&
      f.values.length === 1
      ? f.values[0]
      : undefined;
  };
  const boolVal = (field: string): boolean | undefined => {
    const f = singleChip(field);
    if (f?.type !== "boolean") {
      return undefined;
    }
    // These boolean fields are EQUALS-only, so the chip's value is the answer.
    return f.values.length === 1 ? f.values[0] : undefined;
  };

  return {
    sentiment: enumValues("sentiment") as FeedbackSentiment[] | undefined,
    category: enumValues("category") as FeedbackCategory[] | undefined,
    orgId: enumValues("org_id"),
    createdBy: enumValues("created_by"),
    sessionId: stringSingle("thread_id"),
    resolved: boolVal("resolved"),
    hasNotes: boolVal("has_notes"),
    hasErrors: boolVal("has_errors"),
    isCustomer: boolVal("is_customer"),
    adminLabel: enumValues("admin_label"),
  };
}
